/**
 * B91 GAP-6 — Stripe Connect custody doors (RULED 2026-09-21, Pathik:
 * "platform account + per-tenant connected accounts, webhooks registered
 * centrally; the platform never holds raw tenant keys").
 *
 * Three ADMIN-only doors, all speaking the platform's Connect port — no
 * request field anywhere carries key material, and no response ever does:
 * - stripe.connect.create — mint (or answer) the tenant's ONE connected
 *   account; idempotent by construction.
 * - stripe.connect.link — a provider-hosted onboarding link; the tenant
 *   finishes setup on the provider's own pages.
 * - stripe.connect.status — where custody stands (absent = honestly none).
 *
 * B654 — two more for a venue that ALREADY has Stripe (Connect OAuth onto
 * its own Standard account):
 * - stripe.connect.authorize — Stripe's OAuth URL, the state signed to this
 *   tenant and this admin;
 * - stripe.connect.authorize.complete — the returned code becomes the
 *   venue's acct_ in the same custody row (kind standard). Stripe's OAuth
 *   tokens are never kept and never answered.
 */
import { z } from "zod";
import { readOp, writeOp } from "./common.js";

const zConnectAccount = z
  .object({
    accountId: z.string().min(1),
    chargesEnabled: z.boolean(),
    detailsSubmitted: z.boolean(),
    /** B654 — express (minted by the platform) or standard (the venue's own, via OAuth). */
    kind: z.enum(["express", "standard"]).optional(),
    /** B654 — the venue revoked access from its Stripe dashboard; takes no charges. */
    disconnected: z.literal(true).optional(),
  })
  .strict();

export const stripeConnectCreate = writeOp({
  name: "stripe.connect.create",
  permission: "payment:write",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z.object({ account: zConnectAccount }).strict(),
  exampleRequest: {},
  exampleResponse: { account: { accountId: "acct_1Example", chargesEnabled: false, detailsSubmitted: false } },
});

export const stripeConnectLink = writeOp({
  name: "stripe.connect.link",
  permission: "payment:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    /** Where the provider sends the operator when onboarding completes. */
    returnUrl: z.string().min(1).max(2048),
    /** Where the provider sends them when the link expires mid-flow. */
    refreshUrl: z.string().min(1).max(2048),
  },
  response: z.object({ url: z.string().min(1), expiresAt: z.number().int().optional() }).strict(),
  exampleRequest: { returnUrl: "https://admin.example.com/settings/payments?connect=done", refreshUrl: "https://admin.example.com/settings/payments" },
  exampleResponse: { url: "https://connect.stripe.com/setup/s/example", expiresAt: 1_790_000_000_000 },
});

export const stripeConnectStatus = readOp({
  name: "stripe.connect.status",
  permission: "payment:write",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z.object({ account: zConnectAccount.optional() }).strict(),
  exampleRequest: {},
  exampleResponse: { account: { accountId: "acct_1Example", chargesEnabled: true, detailsSubmitted: true } },
});

export const stripeConnectAuthorize = writeOp({
  name: "stripe.connect.authorize",
  permission: "payment:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    /** Where Stripe returns the admin with ?code&state — must be registered on the platform's Connect settings. */
    returnUrl: z.string().min(1).max(2048),
  },
  response: z.object({ url: z.string().min(1) }).strict(),
  exampleRequest: { returnUrl: "https://admin.example.com/settings/onboarding?stripe=return" },
  exampleResponse: { url: "https://connect.stripe.com/oauth/authorize?response_type=code&client_id=ca_Example&scope=read_write&state=x.y" },
});

export const stripeConnectAuthorizeComplete = writeOp({
  name: "stripe.connect.authorize.complete",
  permission: "payment:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    /** The one-time code Stripe returned (ac_…). */
    code: z.string().min(1).max(256),
    /** The state this tenant's authorize door signed, returned verbatim. */
    state: z.string().min(1).max(2048),
  },
  response: z.object({ account: zConnectAccount }).strict(),
  exampleRequest: { code: "ac_Example", state: "eyJ0ZW5hbnRJZCI6InQifQ.sig" },
  exampleResponse: { account: { accountId: "acct_1Example", chargesEnabled: true, detailsSubmitted: true, kind: "standard" } },
});
